import "@/App.css";
import "@/poa.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import PoaPage from "@/pages/PoaPage";
import ChoicePage from "@/pages/ChoicePage";
import MobileWarningPage from "@/pages/MobileWarningPage";

const SITE_URL = "https://poa.tcauditors.co.za";

// Mobile detection hook (same as the Tax Services Agreement site)
const useIsMobileWithoutDesktopMode = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Check if user agent indicates mobile device
      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // When desktop mode is enabled, the user agent changes to desktop
      // So we only flag as mobile if user agent shows mobile
      setIsMobile(mobileUserAgent);
    };

    checkMobile();
    // Re-check on resize (in case of orientation change or desktop mode toggle)
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Wrapper component that redirects mobile users (same as the Tax Services Agreement site)
const MobileGuard = ({ children }) => {
  const isMobile = useIsMobileWithoutDesktopMode();

  useEffect(() => {
    // Check if user was previously shown the mobile warning
    const wasCaughtOnMobile = sessionStorage.getItem('mobileWarningShown');

    if (wasCaughtOnMobile && !isMobile) {
      // User switched back to desktop mode after being on mobile - redirect to start over
      sessionStorage.removeItem('mobileWarningShown');
      window.location.replace(SITE_URL);
      return;
    }

    if (isMobile) {
      // Use window.location.href to force a full page redirect
      // This clears React Router history so user can't go back
      window.location.href = '/mobile-warning';
    }
  }, [isMobile]);

  // Show nothing while redirecting
  if (isMobile) {
    return null;
  }

  // Check if user was caught and is now trying to access pages
  const wasCaughtOnMobile = sessionStorage.getItem('mobileWarningShown');
  if (wasCaughtOnMobile) {
    sessionStorage.removeItem('mobileWarningShown');
    window.location.replace(SITE_URL);
    return null;
  }

  return children;
};

function App() {
  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          {/* Mobile warning page - accessible without guard */}
          <Route path="/mobile-warning" element={<MobileWarningPage />} />

          {/* Front page: choose personal or company (owner, 2026-10-27) */}
          <Route path="/" element={<MobileGuard><ChoicePage /></MobileGuard>} />

          {/* The single-page SARS Power of Attorney, personal (unchanged) and company */}
          <Route path="/personal" element={<MobileGuard><PoaPage key="personal" variant="personal" /></MobileGuard>} />
          <Route path="/company" element={<MobileGuard><PoaPage key="company" variant="company" /></MobileGuard>} />

          {/* Anything else goes to the POA */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
